import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { fmtDDMMYYYY } from '../../lib/format';
import CrmModal from '../components/CrmModal';
import {
  IconPhone,
  IconWhatsApp,
  IconBuilding,
  IconPackage,
  IconCalendar,

  IconClock,
  IconRenewal,
  IconSearch,
  IconZap,
  IconCheck,
  IconUsers
} from '../components/SvgIcons';

function formatWhatsAppUrl(mobile, clientName, serviceName, renewalDate) {
  let num = (mobile || '').replace(/\D/g, '');
  if (!num) return null;
  if (num.length === 10) num = '91' + num;
  const formattedDate = fmtDDMMYYYY(renewalDate) || renewalDate;
  const msg = `Hello ${clientName},\n\nThis is a polite reminder from Vardhate regarding your service *${serviceName}*.\nYour service renewal date is *${formattedDate}*.\n\nPlease confirm your renewal. Thank you!`;
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
}

function addMonthsToDate(dateStr, months) {
  if (!dateStr) {
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    return d.toISOString().split('T')[0];
  }
  const parts = dateStr.split('-');
  if (parts.length !== 3) {
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    return d.toISOString().split('T')[0];
  }
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  d.setMonth(d.getMonth() + months);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function getExpiryBadge(renewalDateStr) {
  if (!renewalDateStr) {
    return {
      text: `Renewal Date Not Set`,
      bg: '#f1f5f9',
      color: 'var(--crm-text-muted)',
      borderColor: 'var(--crm-border)',
    };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parts = renewalDateStr.split('-');
  if (parts.length !== 3) return null;

  const rDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  rDate.setHours(0, 0, 0, 0);

  const diffTime = rDate.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    const daysAgo = Math.abs(diffDays);
    return {
      text: `EXPIRED ${daysAgo} day${daysAgo > 1 ? 's' : ''} ago!`,
      bg: '#f1f5f9',
      color: 'var(--crm-text-main)',
      borderColor: 'var(--crm-border)',
    };
  } else if (diffDays === 0) {
    return {
      text: `Expires TODAY!`,
      bg: '#f1f5f9',
      color: 'var(--crm-text-main)',
      borderColor: 'var(--crm-border)',
    };
  } else if (diffDays === 1) {
    return {
      text: `Expires Tomorrow`,
      bg: '#f1f5f9',
      color: 'var(--crm-text-muted)',
      borderColor: 'var(--crm-border)',
    };
  } else if (diffDays <= 7) {
    return {
      text: `Expires in ${diffDays} days`,
      bg: '#f0f4ff',
      color: 'var(--crm-emerald)',
      borderColor: '#bbf7d0',
    };
  } else {
    return {
      text: `In ${diffDays} days`,
      bg: '#f0f4ff',
      color: 'var(--crm-emerald)',
      borderColor: '#dbeafe',
    };
  }
}

function RenewalItemRow({ item, color, canViewFinance, onOpenClient, onQuickRenew }) {
  const waUrl = formatWhatsAppUrl(item.mobile, item.clientName, item.serviceName, item.renewalDate);
  const badge = getExpiryBadge(item.renewalDate);
  const cleanMobile = (item.mobile || '').replace(/\D/g, '');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '18px 24px',
        borderBottom: '1px solid #e2e8f0',
        gap: 16,
        background: 'var(--crm-card-bg)',
        flexWrap: 'wrap',
        transition: 'all 0.15s ease',
      }}
      className="cm-renewal-item-row"
    >
      {/* 1. CLIENT NAME & BRAND NAME */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 280, flex: '1 1 300px' }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: 'linear-gradient(135deg, #f0f4ff 0%, #dbeafe 100%)',
            color: 'var(--crm-emerald)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            flexShrink: 0,
            border: '1px solid var(--crm-border)',
            boxShadow: '0 2px 6px rgba(37,99,235,0.08)',
          }}
        >
          <IconUsers size={22} color="#224eae" />
        </div>
        <div>
          {/* Client Name */}
          <div
            style={{ fontSize: '1.1rem', fontWeight: 900, color: 'var(--crm-text-main)', lineHeight: 1.2, cursor: 'pointer' }}
            onClick={() => onOpenClient && onOpenClient(item.clientId)}
            title={`Click to view profile of ${item.clientName}`}
          >
            {item.clientName}
          </div>

          {/* Brand Name / Company & Service Details */}
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {item.company ? (
              <span
                style={{
                  background: 'var(--crm-bg)',
                  color: 'var(--crm-text-main)',
                  border: '1px solid var(--crm-border)',
                  padding: '3px 10px',
                  borderRadius: 8,
                  fontSize: '0.82rem',
                  fontWeight: 800,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <IconBuilding size={14} color="#475569" /> Brand: <strong style={{ color: 'var(--crm-text-main)', marginLeft: 2 }}>{item.company}</strong>
              </span>
            ) : (
              <span style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>No Brand Name</span>
            )}

            {/* Service Name & Quantity */}
            <span
              style={{
                background: 'var(--crm-emerald-light)',
                color: 'var(--crm-emerald)',
                border: '1px solid var(--crm-border)',
                padding: '3px 10px',
                borderRadius: 8,
                fontSize: '0.82rem',
                fontWeight: 800,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <IconPackage size={14} color="#224eae" /> {item.serviceName} {item.quantity > 1 ? `(${item.quantity} ${item.unit || ''})` : ''}
            </span>
          </div>
        </div>
      </div>

      {/* 2. SERVICE EXPIRY DATE & STATUS */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 200, flexShrink: 0 }}>
        {badge && (
          <div>
            <span
              style={{
                background: badge.bg,
                color: badge.color,
                border: `1px solid ${badge.borderColor}`,
                padding: '4px 12px',
                borderRadius: 20,
                fontSize: '0.82rem',
                fontWeight: 900,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <IconClock size={14} color={badge.color} /> {badge.text}
            </span>
          </div>
        )}
        <div style={{ fontSize: '0.88rem', fontWeight: 900, color: 'var(--crm-text-main)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: 'var(--crm-text-muted)', fontWeight: 700 }}>Expiry Date:</span>
          <span style={{ background: 'var(--crm-bg)', padding: '4px 12px', borderRadius: 8, border: '1px solid var(--crm-border)', color: color || '#0f172a', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <IconCalendar size={14} color="#475569" /> {item.renewalDate ? fmtDDMMYYYY(item.renewalDate) : 'Not Set'}
          </span>
        </div>
      </div>

      {/* 3. ACTION BUTTONS: RENEW, CALL, WHATSAPP */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
        {/* RENEW BUTTON */}
        {canViewFinance && (
          <button
            type="button"
            style={{
              fontSize: '0.88rem',
              padding: '9px 20px',
              background: 'linear-gradient(135deg, #224eae 0%, #1c3d8a 100%)',
              color: '#ffffff',
              border: 'none',
              fontWeight: 900,
              borderRadius: 10,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)',
              transition: 'transform 0.15s ease',
            }}
            onClick={() => onQuickRenew(item)}
            title={`Renew service for ${item.clientName}`}
          >
            <IconZap size={16} color="#ffffff" /> Renew
          </button>
        )}

        {/* CALL BUTTON */}
        {cleanMobile && (
          <a
            href={`tel:${cleanMobile}`}
            style={{
              fontSize: '0.88rem',
              padding: '9px 16px',
              background: 'var(--crm-card-bg)',
              color: 'var(--crm-text-main)',
              border: '1px solid var(--crm-border)',
              textDecoration: 'none',
              fontWeight: 800,
              borderRadius: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
            }}
            title={`Call ${item.clientName} (${cleanMobile})`}
          >
            <IconPhone size={16} color="#0f172a" /> Call
          </a>
        )}

        {/* WHATSAPP BUTTON */}
        {waUrl && (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '0.88rem',
              padding: '9px 18px',
              background: '#224eae',
              color: '#ffffff',
              border: 'none',
              textDecoration: 'none',
              fontWeight: 900,
              borderRadius: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
            }}
            title="Send WhatsApp Reminder"
          >
            <IconWhatsApp size={16} color="#ffffff" /> WhatsApp
          </a>
        )}
      </div>
    </div>
  );
}

function RenewalCard({ title, items, color, bgColor, canViewFinance, onOpenClient, onQuickRenew }) {
  if (items.length === 0) return null;
  return (
    <div
      style={{
        background: 'var(--crm-card-bg)',
        border: `1px solid ${color}35`,
        borderRadius: 20,
        overflow: 'hidden',
        marginBottom: 24,
        boxShadow: '0 10px 30px -5px rgba(0,0,0,0.04)',
      }}
    >
      <div
        style={{
          background: bgColor,
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `2px solid ${color}25`,
        }}
      >
        <div style={{ fontWeight: 900, color, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 10 }}>
          {title}
        </div>
        <span
          style={{
            background: color,
            color: '#ffffff',
            borderRadius: 30,
            padding: '4px 16px',
            fontSize: '0.82rem',
            fontWeight: 900,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          {items.length} Service{items.length > 1 ? 's' : ''}
        </span>
      </div>
      <div>
        {items.map((item) => (
          <RenewalItemRow
            key={`${item.clientId}-${item.orderId}`}
            item={item}
            color={color}
            canViewFinance={canViewFinance}
            onOpenClient={onOpenClient}
            onQuickRenew={onQuickRenew}
          />
        ))}
      </div>
    </div>
  );
}

export default function RenewalDashboard({ currency, canViewFinance = true, onOpenClient }) {
  const [renewals, setRenewals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [renewingItem, setRenewingItem] = useState(null);

  // Quick Renew Form state
  const [renewForm, setRenewForm] = useState({
    newRenewalDate: '',
    amountReceived: '',
    resetPayment: false,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadRenewals = () => {
    setLoading(true);
    api.get('/api/client-module/renewals')
      .then((data) => setRenewals(data))
      .catch(() => setRenewals(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRenewals();
  }, []);

  const handleOpenQuickRenew = (item) => {
    setRenewingItem(item);
    const defaultNewDate = addMonthsToDate(item.renewalDate, 1);
    setRenewForm({
      newRenewalDate: defaultNewDate,
      amountReceived: item.finalAmount || '',
      resetPayment: true,
      notes: 'Renewed for 1 month',
    });
    setError('');
  };

  const handleAddPresetMonths = (months) => {
    if (!renewingItem) return;
    const date = addMonthsToDate(renewingItem.renewalDate, months);
    setRenewForm((prev) => ({
      ...prev,
      newRenewalDate: date,
      notes: `Renewed for ${months} month(s)`,
    }));
  };

  const handleConfirmRenew = async (e) => {
    e?.preventDefault();
    if (!renewingItem) return;
    if (!renewForm.newRenewalDate) {
      setError('Please select a valid new renewal date');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.post(`/api/client-module/renewals/${renewingItem.clientId}/orders/${renewingItem.orderId}/renew`, {
        newRenewalDate: renewForm.newRenewalDate,
        amountReceived: renewForm.resetPayment ? Number(renewForm.amountReceived) || 0 : undefined,
        resetPayment: renewForm.resetPayment,
        notes: renewForm.notes,
      });
      setRenewingItem(null);
      loadRenewals();
    } catch (err) {
      setError(err.message || 'Failed to renew service');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 80, textAlign: 'center', color: 'var(--crm-text-muted)', fontWeight: 700, fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
        <IconClock size={24} /> Loading renewal data…
      </div>
    );
  }

  if (!renewals) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--crm-text-main)' }}>Failed to load renewals. Check server connection.</div>;
  }

  const buckets = {
    overdue: renewals.overdue || [],
    today: renewals.today || [],
    tomorrow: renewals.tomorrow || [],
    next7Days: renewals.next7Days || [],
    next30Days: renewals.next30Days || [],
    future: renewals.future || [],
    noDate: renewals.noDate || [],
  };

  const filterMatchesSearch = (item) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (item.clientName && item.clientName.toLowerCase().includes(q)) ||
      (item.company && item.company.toLowerCase().includes(q)) ||
      (item.serviceName && item.serviceName.toLowerCase().includes(q)) ||
      (item.mobile && item.mobile.includes(q))
    );
  };

  const filteredBuckets = {
    overdue: buckets.overdue.filter(filterMatchesSearch),
    today: buckets.today.filter(filterMatchesSearch),
    tomorrow: buckets.tomorrow.filter(filterMatchesSearch),
    next7Days: buckets.next7Days.filter(filterMatchesSearch),
    next30Days: buckets.next30Days.filter(filterMatchesSearch),
    future: buckets.future.filter(filterMatchesSearch),
    noDate: buckets.noDate.filter(filterMatchesSearch),
  };

  const totalOverdue = buckets.overdue.length;
  const totalToday = buckets.today.length;
  const totalTomorrow = buckets.tomorrow.length;
  const totalNext7 = buckets.next7Days.length;
  const totalNext30 = buckets.next30Days.length;
  const totalFuture = buckets.future.length;
  const totalNoDate = buckets.noDate.length;
  const totalAll = totalOverdue + totalToday + totalTomorrow + totalNext7 + totalNext30 + totalFuture + totalNoDate;

  return (
    <div style={{ width: '100%', fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif" }}>
      {/* Premium Header Banner */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #0f172a 50%, #224eae 100%)',
          borderRadius: 24,
          padding: '28px 36px',
          color: '#ffffff',
          marginBottom: 28,
          boxShadow: '0 16px 36px -10px rgba(15, 23, 42, 0.3)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontWeight: 900, fontSize: '1.7rem', color: '#ffffff', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconRenewal size={26} color="#38bdf8" /> Renewal & Expiration Management
          </h2>
          <p style={{ margin: '6px 0 0', opacity: 0.88, fontSize: '0.95rem' }}>
            Track active service renewals, upcoming expiration deadlines, and client communications seamlessly.
          </p>
        </div>
        <div
          style={{
            background: 'rgba(255,255,255,0.12)',
            backdropFilter: 'blur(10px)',
            padding: '10px 20px',
            borderRadius: 16,
            fontSize: '0.88rem',
            fontWeight: 800,
            border: '1px solid rgba(255,255,255,0.18)',
          }}
        >
          Total Active Services: <strong style={{ color: '#38bdf8', fontSize: '1.1rem', marginLeft: 6 }}>{totalAll}</strong>
        </div>
      </div>

      {/* Summary Filter Cards */}
      <div className="cm-ref-stats-row" style={{ marginBottom: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14 }}>
        <div
          className={`cm-ref-stat-card ${activeFilter === 'all' ? 'active' : ''}`}
          style={{ borderTop: '4px solid #224eae', cursor: 'pointer', background: activeFilter === 'all' ? '#f0f9ff' : '#ffffff', borderRadius: 16, padding: '16px 18px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}
          onClick={() => setActiveFilter('all')}
        >
          <div className="cm-ref-stat-label" style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--crm-text-muted)' }}>ALL SERVICES</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--crm-emerald)', fontSize: '1.8rem', fontWeight: 900, marginTop: 4 }}>{totalAll}</div>
        </div>
        <div
          className={`cm-ref-stat-card ${activeFilter === 'overdue' ? 'active' : ''}`}
          style={{ borderTop: '4px solid #0f172a', cursor: 'pointer', background: activeFilter === 'overdue' ? '#f1f5f9' : '#ffffff', borderRadius: 16, padding: '16px 18px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}
          onClick={() => setActiveFilter('overdue')}
        >
          <div className="cm-ref-stat-label" style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--crm-text-muted)' }}>OVERDUE</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--crm-text-main)', fontSize: '1.8rem', fontWeight: 900, marginTop: 4 }}>{totalOverdue}</div>
        </div>
        <div
          className={`cm-ref-stat-card ${activeFilter === 'today' ? 'active' : ''}`}
          style={{ borderTop: '4px solid #0f172a', cursor: 'pointer', background: activeFilter === 'today' ? '#f1f5f9' : '#ffffff', borderRadius: 16, padding: '16px 18px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}
          onClick={() => setActiveFilter('today')}
        >
          <div className="cm-ref-stat-label" style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--crm-text-muted)' }}>DUE TODAY</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--crm-text-main)', fontSize: '1.8rem', fontWeight: 900, marginTop: 4 }}>{totalToday}</div>
        </div>
        <div
          className={`cm-ref-stat-card ${activeFilter === 'tomorrow' ? 'active' : ''}`}
          style={{ borderTop: '4px solid #0f172a', cursor: 'pointer', background: activeFilter === 'tomorrow' ? '#f1f5f9' : '#ffffff', borderRadius: 16, padding: '16px 18px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}
          onClick={() => setActiveFilter('tomorrow')}
        >
          <div className="cm-ref-stat-label" style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--crm-text-muted)' }}>DUE TOMORROW</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--crm-text-main)', fontSize: '1.8rem', fontWeight: 900, marginTop: 4 }}>{totalTomorrow}</div>
        </div>
        <div
          className={`cm-ref-stat-card ${activeFilter === 'next7Days' ? 'active' : ''}`}
          style={{ borderTop: '4px solid #224eae', cursor: 'pointer', background: activeFilter === 'next7Days' ? '#f0f4ff' : '#ffffff', borderRadius: 16, padding: '16px 18px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}
          onClick={() => setActiveFilter('next7Days')}
        >
          <div className="cm-ref-stat-label" style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--crm-text-muted)' }}>NEXT 7 DAYS</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--crm-emerald)', fontSize: '1.8rem', fontWeight: 900, marginTop: 4 }}>{totalNext7}</div>
        </div>
        <div
          className={`cm-ref-stat-card ${activeFilter === 'next30Days' ? 'active' : ''}`}
          style={{ borderTop: '4px solid #224eae', cursor: 'pointer', background: activeFilter === 'next30Days' ? '#f0f4ff' : '#ffffff', borderRadius: 16, padding: '16px 18px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}
          onClick={() => setActiveFilter('next30Days')}
        >
          <div className="cm-ref-stat-label" style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--crm-text-muted)' }}>NEXT 30 DAYS</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--crm-emerald)', fontSize: '1.8rem', fontWeight: 900, marginTop: 4 }}>{totalNext30}</div>
        </div>
        <div
          className={`cm-ref-stat-card ${activeFilter === 'future' ? 'active' : ''}`}
          style={{ borderTop: '4px solid #224eae', cursor: 'pointer', background: activeFilter === 'future' ? '#f5f3ff' : '#ffffff', borderRadius: 16, padding: '16px 18px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}
          onClick={() => setActiveFilter('future')}
        >
          <div className="cm-ref-stat-label" style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--crm-text-muted)' }}>FUTURE</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--crm-emerald)', fontSize: '1.8rem', fontWeight: 900, marginTop: 4 }}>{totalFuture}</div>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
          <span style={{ position: 'absolute', left: 14, color: 'var(--crm-text-muted)', display: 'flex' }}>
            <IconSearch size={18} />
          </span>
          <input
            type="text"
            className="cm-input"
            placeholder="Search client, brand name, or service name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '12px 18px 12px 42px', borderRadius: 14, border: '1px solid var(--crm-border)', fontSize: '0.95rem', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}
          />
        </div>
        {activeFilter !== 'all' && (
          <button
            type="button"
            className="cm-btn-outline"
            style={{ fontSize: '0.88rem', padding: '10px 18px', borderRadius: 12 }}
            onClick={() => setActiveFilter('all')}
          >
            Show All Active ({totalAll})
          </button>
        )}
      </div>

      {totalAll === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--crm-card-bg)', borderRadius: 20, border: '2px dashed #cbd5e1' }}>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
            <IconCheck size={42} color="#224eae" />
          </div>
          <div style={{ fontWeight: 900, fontSize: '1.1rem', color: 'var(--crm-text-main)' }}>No active services found</div>
          <div style={{ color: 'var(--crm-text-muted)', marginTop: 6, fontSize: '0.9rem' }}>Add service orders to clients to track renewals here</div>
        </div>
      ) : (
        <>
          {(activeFilter === 'all' || activeFilter === 'overdue') && (
            <RenewalCard
              title="Overdue Renewals"
              items={filteredBuckets.overdue}
              color="#0f172a"
              bgColor="#f1f5f9"
              canViewFinance={canViewFinance}
              onOpenClient={onOpenClient}
              onQuickRenew={handleOpenQuickRenew}
            />
          )}
          {(activeFilter === 'all' || activeFilter === 'today') && (
            <RenewalCard
              title="Due Today"
              items={filteredBuckets.today}
              color="#0f172a"
              bgColor="#f1f5f9"
              canViewFinance={canViewFinance}
              onOpenClient={onOpenClient}
              onQuickRenew={handleOpenQuickRenew}
            />
          )}
          {(activeFilter === 'all' || activeFilter === 'tomorrow') && (
            <RenewalCard
              title="Due Tomorrow"
              items={filteredBuckets.tomorrow}
              color="#0f172a"
              bgColor="#f1f5f9"
              canViewFinance={canViewFinance}
              onOpenClient={onOpenClient}
              onQuickRenew={handleOpenQuickRenew}
            />
          )}
          {(activeFilter === 'all' || activeFilter === 'next7Days') && (
            <RenewalCard
              title="Next 7 Days"
              items={filteredBuckets.next7Days}
              color="#224eae"
              bgColor="#f0f4ff"
              canViewFinance={canViewFinance}
              onOpenClient={onOpenClient}
              onQuickRenew={handleOpenQuickRenew}
            />
          )}
          {(activeFilter === 'all' || activeFilter === 'next30Days') && (
            <RenewalCard
              title="Next 30 Days"
              items={filteredBuckets.next30Days}
              color="#224eae"
              bgColor="#f0f4ff"
              canViewFinance={canViewFinance}
              onOpenClient={onOpenClient}
              onQuickRenew={handleOpenQuickRenew}
            />
          )}
          {(activeFilter === 'all' || activeFilter === 'future') && (
            <RenewalCard
              title="Future Renewals (31+ Days Onwards)"
              items={filteredBuckets.future}
              color="#224eae"
              bgColor="#f5f3ff"
              canViewFinance={canViewFinance}
              onOpenClient={onOpenClient}
              onQuickRenew={handleOpenQuickRenew}
            />
          )}
          {(activeFilter === 'all' || activeFilter === 'noDate') && (
            <RenewalCard
              title="Active Services Missing Renewal Date"
              items={filteredBuckets.noDate}
              color="#64748b"
              bgColor="#f1f5f9"
              canViewFinance={canViewFinance}
              onOpenClient={onOpenClient}
              onQuickRenew={handleOpenQuickRenew}
            />
          )}
        </>
      )}

      {/* Quick Renew Modal */}
      {renewingItem && (
        <CrmModal
          onClose={() => setRenewingItem(null)}
          title={`Quick Service Renewal — ${renewingItem.clientName}`}
          subtitle={`Service: ${renewingItem.serviceName}`}
          maxWidth={540}
        >
          <form onSubmit={handleConfirmRenew}>
            {error && <div className="err" style={{ marginBottom: 14, fontWeight: 700 }}>{error}</div>}

            <div className="cm-form-section-card">
              <div className="cm-field-group">
                <label>Add Preset Duration (+)</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="cm-btn-outline" style={{ fontSize: '0.82rem', padding: '6px 14px' }} onClick={() => handleAddPresetMonths(1)}>
                    +1 Month
                  </button>
                  <button type="button" className="cm-btn-outline" style={{ fontSize: '0.82rem', padding: '6px 14px' }} onClick={() => handleAddPresetMonths(3)}>
                    +3 Months
                  </button>
                  <button type="button" className="cm-btn-outline" style={{ fontSize: '0.82rem', padding: '6px 14px' }} onClick={() => handleAddPresetMonths(6)}>
                    +6 Months
                  </button>
                  <button type="button" className="cm-btn-outline" style={{ fontSize: '0.82rem', padding: '6px 14px' }} onClick={() => handleAddPresetMonths(12)}>
                    +1 Year
                  </button>
                </div>
              </div>

              <div className="cm-field-group">
                <label>New Renewal Date *</label>
                <input
                  type="date"
                  required
                  value={renewForm.newRenewalDate}
                  onChange={(e) => setRenewForm({ ...renewForm, newRenewalDate: e.target.value })}
                />
              </div>

              <div className="cm-field-group">
                <label>
                  <input
                    type="checkbox"
                    checked={renewForm.resetPayment}
                    onChange={(e) => setRenewForm({ ...renewForm, resetPayment: e.target.checked })}
                    style={{ marginRight: 8 }}
                  />
                  Record Payment Received & Reset Status to Paid
                </label>
              </div>

              {renewForm.resetPayment && (
                <div className="cm-field-group">
                  <label>Amount Received (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={renewForm.amountReceived}
                    onChange={(e) => setRenewForm({ ...renewForm, amountReceived: e.target.value })}
                  />
                </div>
              )}

              <div className="cm-field-group">
                <label>Renewal Note / Remarks</label>
                <input
                  type="text"
                  placeholder="e.g. Renewed for next 3 months, payment received in GPay"
                  value={renewForm.notes}
                  onChange={(e) => setRenewForm({ ...renewForm, notes: e.target.value })}
                />
              </div>
            </div>

            <div className="cm-modal-footer" style={{ marginTop: 20 }}>
              <button type="button" className="cm-btn-outline" onClick={() => setRenewingItem(null)}>
                Cancel
              </button>
              <button type="button" className="cm-btn-emerald" onClick={handleConfirmRenew} disabled={saving}>
                {saving ? 'Saving…' : 'Confirm Renewal'}
              </button>
            </div>
          </form>
        </CrmModal>
      )}
    </div>
  );
}
